import { USER_CURRENT_KEY } from "../api/public-voissa/users";
import { useQueryClient } from "@tanstack/react-query";
import createLink from "../libs/createLink";
import { isCertifEligibleGender } from "../const/certification";
import { useNavigate } from "react-router-dom";
import { ModalContext } from "../ui/components2/ModalManager";
import { useContext } from "react";

export const USER_LOCKED_LABEL = {
    ROLE_GUEST: "ROLE_GUEST",
    ROLE_EMAIL_UNCONFIRMED: "ROLE_EMAIL_UNCONFIRMED",
    ROLE_BANNED: "ROLE_BANNED",
    ROLE_NO_ACCESS: "ROLE_NO_ACCESS",
    ROLE_NO_PREMIUM: "ROLE_NO_PREMIUM",
    USER_WARNING: "USER_WARNING",
};

export const ACTION_LOCKED = {
    CREATE_LIKE: "CREATE_LIKE",
    CREATE_BOOKMARK: "CREATE_BOOKMARK",
    CREATE_FOLLOW: "CREATE_FOLLOW",
    CREATE_FRIEND: "CREATE_FRIEND",
    CREATE_STATUS: "CREATE_STATUS",
    UPLOAD: "UPLOAD",
    MESSENGER: "MESSENGER",
    PREMIUM: "PREMIUM"
};

/**
 * ✅ Actions réellement premium-only
 * Tout le reste ne dépend QUE de hasAccess
 */
const PREMIUM_ONLY_ACTIONS = new Set([
    ACTION_LOCKED.PREMIUM,
    // ajoute ici si nécessaire
]);

const useCurrentUserActionLocked = ({ action, contentOwner }) => {

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { openModal } = useContext(ModalContext);

    const currentUser = queryClient.getQueryData([USER_CURRENT_KEY]);
    const currentRole = currentUser?.role || "guest";

    let lockedReason = null;

    let isGuest = null;
    let isEmailUnconfirmed = null;
    let isBanned = null;
    let isWarning = null;
    let isNoAccess = null;
    let isNoPremium = null;

    if (currentUser?._id) {

        isEmailUnconfirmed =
            currentRole === "emailUnconfirmed"
                ? USER_LOCKED_LABEL.ROLE_EMAIL_UNCONFIRMED
                : null;

        isBanned =
            currentRole === "banned"
                ? USER_LOCKED_LABEL.ROLE_BANNED
                : null;

        isWarning =
            currentUser?.restrictions?.blockAllContentCreation
                ? USER_LOCKED_LABEL.USER_WARNING
                : null;

        // ✅ Accès global (comme avant avec hasAccess)
        isNoAccess =
            currentUser?.subscription?.hasAccess
                ? null
                : USER_LOCKED_LABEL.ROLE_NO_ACCESS;

        // ✅ Premium uniquement si l’action est premium-only
        isNoPremium =
            PREMIUM_ONLY_ACTIONS.has(action) &&
            !currentUser?.subscription?.isPremium
                ? USER_LOCKED_LABEL.ROLE_NO_PREMIUM
                : null;
    }
    else {
        isGuest =
            currentRole === "guest"
                ? USER_LOCKED_LABEL.ROLE_GUEST
                : null;
    }

    // ordre important : access avant premium
    lockedReason =
        isGuest ||
        isBanned ||
        isEmailUnconfirmed ||
        isWarning ||
        isNoAccess ||
        isNoPremium;

    const actionDefault = () => {
        if (isCertifEligibleGender(currentUser?.profile?.gender)) {
            navigate(createLink("userMeSettingsCertification", currentUser));
        }
        else if (currentUser?._id) {
            navigate(createLink("subscription", currentUser));
        }
        else {
            openModal("Signin", {
                lockedContentOwner: contentOwner,
                lockedReason,
                lockedAction: action
            });
        }
    };

    function handleLockedAction() {

        if (!lockedReason) return false;

        switch (lockedReason) {

            case USER_LOCKED_LABEL.ROLE_GUEST:
                openModal("Signin", {
                    lockedContentOwner: contentOwner,
                    lockedReason,
                    lockedAction: action
                });
                break;

            case USER_LOCKED_LABEL.ROLE_EMAIL_UNCONFIRMED:
                openModal("EncourageEmailConfirm", { forceOpen: true });
                break;

            case USER_LOCKED_LABEL.ROLE_BANNED:
                navigate(createLink("signin"));
                break;

            case USER_LOCKED_LABEL.USER_WARNING:
                openModal("ModalUserRestrictions", {
                    toggle: true,
                    isModal: true,
                    userId: currentUser?._id,
                    username: currentUser?.username
                });
                break;

            case USER_LOCKED_LABEL.ROLE_NO_ACCESS:
            case USER_LOCKED_LABEL.ROLE_NO_PREMIUM:
                actionDefault();
                break;

            default:
                return false;
        }

        return true;
    }

    return {
        locked: !!lockedReason,
        lockedReason,
        handleLockedAction
    };
};

export default useCurrentUserActionLocked;